// Validation and approval workflow (PRD §4.4, US-25, US-26): draft → submitted → reviewed → validated → released, or
// rejected. Each step is an append-only review event stamped by the database with the caller's identity; the database
// refuses any step past "submitted" by the person who entered the value (separation of duties). The screen filters by
// batch, organisation and status, and says on every row why the signed-in user can or cannot act on it.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { PageHeader } from '../components/Layout.tsx';
import { Enum, ErrorBox, fmtValue, Loading } from '../components/ui.tsx';
import { batchChain } from '../lib/chain.ts';
import { activePack, productBatches, profiles, reviewsFor, whoAmI } from '../lib/data.ts';
import { operatorClient } from '../lib/supabase.ts';
import { fieldDef, ownedBy, type FieldDef, type Org } from '../lib/template.ts';
import type { NodeRow, ReviewRow, ValueRow } from '../lib/types.ts';

const NEXT: Record<string, string[]> = {
  draft: ['submitted'],
  submitted: ['reviewed', 'rejected'],
  reviewed: ['validated', 'rejected'],
  validated: ['released', 'rejected'],
  released: [],
  rejected: [],
};
const REVIEWER_ROLES = ['quality_manager', 'regulatory_owner', 'platform_admin'];
const STATUS_FILTERS = ['open', 'released', 'rejected', 'all'] as const;
const PAGE = 50;
// enough for the demo dataset and for one consortium's batches; filters narrow it further in the browser
const FETCH_LIMIT = 2000;

export function Values() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState<ValueRow[] | null>(null);
  const [reviews, setReviews] = useState<Map<number, ReviewRow[]>>(new Map());
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const [me, setMe] = useState<{ id: string | null; org: string | null; roles: string[] } | null>(null);
  const [fields, setFields] = useState<FieldDef[]>([]);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [batches, setBatches] = useState<NodeRow[]>([]);
  const [shown, setShown] = useState(PAGE);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState('');

  const reviewer = !!me && me.roles.some((r) => REVIEWER_ROLES.includes(r));
  const batch = params.get('batch') ?? params.get('node') ?? '';
  // a reviewer starts on every organisation; anyone else on their own
  const orgCode = params.get('org') ?? (me && !reviewer ? (me.org ?? '') : '');
  const status = (params.get('status') ?? 'open') as (typeof STATUS_FILTERS)[number];
  const mineOnly = params.get('mine') === '1';
  const placeholders = params.get('empty') === '1';
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    if (k === 'batch') next.delete('node');
    setParams(next, { replace: true });
    setShown(PAGE);
  };

  useEffect(() => {
    void (async () => {
      try {
        const [pack, who, o, list, user] = await Promise.all([
          activePack(),
          whoAmI(),
          operatorClient().from('organisations').select('org_code, name, aliases').order('name'),
          productBatches(),
          operatorClient().auth.getUser(),
        ]);
        setFields(
          (pack?.pack as unknown as { fields: { items: FieldDef[] } } | undefined)?.fields.items ?? [],
        );
        setOrgs((o.data ?? []) as Org[]);
        setBatches(list);
        setMe({ id: user.data.user?.id ?? null, org: who?.org ?? null, roles: who?.roles ?? [] });
        setNames(await profiles());
      } catch (e) {
        setError(e);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    try {
      let q = operatorClient().from('current_values').select('*').order('entered_at', { ascending: false });
      if (batch) {
        // a batch, or any node or product id typed into an older link
        const { steps, prodCode } = await batchChain(batch);
        const ids = steps.map((s) => s.id).filter((x): x is string => !!x);
        q = q.in('node_id', ids.length > 1 || prodCode ? [...ids, ...(prodCode ? [prodCode] : [])] : [batch]);
      }
      if (status === 'open') q = q.not('validation_status', 'in', '(released,rejected)');
      else if (status !== 'all') q = q.eq('validation_status', status);
      const { data, error: err } = await q.limit(FETCH_LIMIT);
      if (err) throw new Error(err.message);
      const list = (data ?? []) as ValueRow[];
      setRows(list);
      setReviews(await reviewsFor(list.map((v) => v.value_id)));
    } catch (e) {
      setError(e);
    }
  }, [batch, status]);
  useEffect(() => {
    void load();
  }, [load]);

  const org = orgs.find((o) => o.org_code === orgCode) ?? null;
  const visible = useMemo(
    () =>
      (rows ?? []).filter(
        (v) =>
          (placeholders || v.value !== null) &&
          (!mineOnly || (me?.id && v.entered_by === me.id)) &&
          (!org || ownedBy(fieldDef(fields, v.field_id)?.source_partner ?? '', org)),
      ),
    [rows, placeholders, mineOnly, me, org, fields],
  );

  async function step(v: ValueRow, next: string) {
    setError(null);
    setNotice('');
    const { error: err } = await operatorClient()
      .from('value_reviews')
      .insert({
        value_id: v.value_id,
        validation_status: next,
        note: `${next} in the operator interface`,
      });
    if (err) setError(new Error(`${v.node_id} ${v.field_id} → ${next}: ${err.message}`));
    else
      setNotice(
        t('values.recorded', { field: fieldName(v.field_id), status: t(`validation.${next}`, next) }),
      );
    await load();
  }

  if (!rows || !me) return <Loading />;
  const who = (id: string | null | undefined) => (id ? (names.get(id) ?? id.slice(0, 8)) : '—');
  const fieldName = (fid: string) => {
    const def = fieldDef(fields, fid);
    if (!def) return fid;
    return def.field_id === fid ? def.field_name : `${def.field_name}: ${fid.split(/[._]/).pop() ?? ''}`;
  };
  const when = (ts: string) => ts.slice(0, 16).replace('T', ' ');
  const roleHint = reviewer ? 'reviewer' : me.roles.includes('data_provider') ? 'provider' : 'reader';

  /** Why this user has no button on this row, in words. */
  const why = (v: ValueRow, own: boolean): string => {
    if (v.value === null) return t('values.why.placeholder');
    if (v.validation_status === 'released') return t('values.why.released');
    if (v.validation_status === 'rejected') return t('values.why.rejected');
    if (v.validation_status === 'draft') return t('values.why.not_yours');
    if (own) return t('values.why.own');
    return t('values.why.reviewers');
  };

  return (
    <main>
      <PageHeader title={t('values.title')} page="values" />
      <p className="hint">{t(`values.role.${roleHint}`)}</p>
      <div className="form filters-row">
        <label>
          {t('col.batch')}
          <select value={batch} onChange={(e) => set('batch', e.target.value)}>
            <option value="">{t('values.all_batches')}</option>
            {batches.map((b) => (
              <option key={b.node_id} value={b.node_id}>
                {b.node_id} · {b.prod_code}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('values.org')}
          <select value={orgCode} onChange={(e) => set('org', e.target.value)}>
            <option value="">{t('values.all_orgs')}</option>
            {orgs.map((o) => (
              <option key={o.org_code} value={o.org_code}>
                {o.name}
                {o.org_code === me.org ? ` (${t('values.mine')})` : ''}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('values.status')}
          <select
            value={status}
            onChange={(e) => set('status', e.target.value === 'open' ? '' : e.target.value)}
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>
                {t(`values.filter.${s}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={mineOnly}
            onChange={(e) => set('mine', e.target.checked ? '1' : '')}
          />{' '}
          {t('values.mine_only')}
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={placeholders}
            onChange={(e) => set('empty', e.target.checked ? '1' : '')}
          />{' '}
          {t('values.show_placeholders')}
        </label>
      </div>
      <ErrorBox error={error} />
      {notice && <p className="ok">{notice}</p>}
      <p className="muted small">{t('values.count', { count: visible.length })}</p>
      {visible.length === 0 ? (
        <p className="muted">{t('values.empty')}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t('values.field')}</th>
              <th>{t('values.node')}</th>
              <th>{t('values.value')}</th>
              <th>{t('values.status')}</th>
              <th>{t('values.entered')}</th>
              <th>{t('values.history')}</th>
              <th className="wide">{t('values.action_col')}</th>
            </tr>
          </thead>
          <tbody>
            {visible.slice(0, shown).map((v) => {
              const own = me.id !== null && v.entered_by === me.id;
              const next =
                v.value === null
                  ? []
                  : (NEXT[v.validation_status] ?? []).filter(
                      (s) =>
                        (s === 'submitted' && (own || reviewer)) || (s !== 'submitted' && reviewer && !own),
                    );
              const history = reviews.get(v.value_id) ?? [];
              return (
                <tr key={v.value_id}>
                  <td>
                    {fieldName(v.field_id)}
                    <div className="small muted">
                      <code>{v.field_id}</code> · {t('col.version_short')} {v.version}
                    </div>
                  </td>
                  <td className="nowrap">
                    {v.node_id}
                    <div className="small muted">{t(`kind.${v.node_kind}`, v.node_kind)}</div>
                  </td>
                  <td>
                    {fmtValue(v.value)}
                    {v.unit ? ` ${v.unit}` : ''}
                    {v.source_document_ref && <div className="small muted">{v.source_document_ref}</div>}
                  </td>
                  <td>
                    <span className={`tag tag-${v.validation_status}`}>
                      <Enum ns="validation" v={v.validation_status} />
                    </span>
                  </td>
                  <td className="small">
                    {who(v.entered_by)}
                    {own && <strong> ({t('values.you')})</strong>}
                    <div className="muted">{when(v.entered_at)}</div>
                  </td>
                  <td className="small">
                    {history.length === 0
                      ? '—'
                      : history.map((r) => (
                          <div key={r.review_id}>
                            <Enum ns="validation" v={r.validation_status} /> ·{' '}
                            {r.system ? `${t('values.system')}: ${r.note ?? ''}` : who(r.reviewed_by)}{' '}
                            <span className="muted">{when(r.reviewed_at)}</span>
                          </div>
                        ))}
                  </td>
                  <td>
                    {next.map((s) => (
                      <button
                        key={s}
                        className={s === 'rejected' ? 'secondary' : ''}
                        onClick={() => void step(v, s)}
                      >
                        {t(`values.action.${s}`)}
                      </button>
                    ))}
                    {next.length === 0 && <div className="small muted">{why(v, own)}</div>}
                    {next.length === 0 && (v.value === null || v.validation_status === 'rejected') && (
                      <Link
                        className="small"
                        to={batch ? `/ingest?batch=${encodeURIComponent(batch)}` : '/ingest'}
                      >
                        {t('nav.ingest')}
                      </Link>
                    )}
                    {own && reviewer && v.validation_status !== 'draft' && next.length === 0 && (
                      <div className="small muted">{t('values.sod_hint')}</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {visible.length > shown && (
        <button className="secondary" onClick={() => setShown(shown + PAGE)}>
          {t('values.more', { count: Math.min(PAGE, visible.length - shown) })}
        </button>
      )}
    </main>
  );
}
